import { useState, useCallback, useEffect } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@shadcn-in-nx/ui/card";
import { Loader2 } from "lucide-react";

export default function AnalyticsTab() {
    const { t } = useTranslation();
    const [errorStats, setErrorStats] = useState(null);
    const [outcomeStats, setOutcomeStats] = useState(null);

    const { call: fetchErrorStats, isLoading: loadingErrors } = useBluelagoon({
        method: "getIntelligenceErrorStats",
    });
    const { call: fetchOutcomeStats, isLoading: loadingOutcomes } = useBluelagoon({
        method: "getIntelligenceOutcomeStats",
    });

    const load = useCallback(async () => {
        const [errRes, outRes] = await Promise.all([
            fetchErrorStats(),
            fetchOutcomeStats(),
        ]);
        if (errRes?.data) setErrorStats(errRes.data);
        if (outRes?.data) setOutcomeStats(outRes.data);
    }, [fetchErrorStats, fetchOutcomeStats]);

    useEffect(() => { load(); }, [load]);

    if ((loadingErrors || loadingOutcomes) && !errorStats) {
        return (
            <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="space-y-6 mt-4">
            <h3 className="text-lg font-semibold">
                {t("admin.intelligence.analytics.errorBreakdown")}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {errorStats && Object.entries(errorStats.byType || {}).map(([type, count]) => (
                    <Card key={type}>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-xs font-medium text-muted-foreground truncate">
                                {type}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-2xl font-bold">{count}</p>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <h3 className="text-lg font-semibold">
                {t("admin.intelligence.analytics.outcomeBreakdown")}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {outcomeStats && Object.entries(outcomeStats.byOutcome || {}).map(([outcome, count]) => (
                    <Card key={outcome}>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-xs font-medium text-muted-foreground truncate">
                                {outcome}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-2xl font-bold">{count}</p>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {outcomeStats && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                                {t("admin.intelligence.outcomes.acceptanceRate")}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-3xl font-bold">{outcomeStats.acceptanceRate}%</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                                {t("admin.intelligence.outcomes.avgIterations")}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-3xl font-bold">{outcomeStats.avgIterations}</p>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}
